import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import withAdminAuth from "../../components/HOC/withAdminAuthPage";
import { AssetBreadCrumb, PageContainer } from "../../components/AssetBreadCrumb";
import { ReactComponent as BriefcaseIcon } from "../../assets/icons/briefcase-02.svg";
import ProjectCard from "./ProjectCard";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { fetchProjects } from "../../store/projectSlice";
import LoadingIcon from "../../components/LoadingIcon";
import type { AppDispatch, RootState } from "../../store";

const ProjectsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { projects, isLoading } = useSelector((state: RootState) => state.projects);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  const handleGetStarted = () => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.addProject,
      properties: { __modalSize: "sm" },
    }));
  };

  const handleDelete = (id: string, title: string) => {
    dispatch(setModalState({
      modalOpen: true,
      modalView: modalViewsConst.generalDelete,
      properties: {
        __modalSize: "sm",
        title: "Delete Project",
        itemName: title,
        itemId: id,
        itemType: "project",
      },
    }));
  };

  return (
    <PageContainer fullHeight>
      <AssetBreadCrumb assetGroupName="Projects" />
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        <div className="shrink-0">
          <div className="flex items-start gap-3 p-5">
            <div className="flex-1">
              <div className="text-gray-900 text-xl font-semibold font-Inter mb-1">
                Projects
              </div>
              <div className="text-gray-500 text-sm font-normal font-Inter">
                Organise your work into projects. Each project gets AI-generated tasks, datasets, models, schedules, and a dedicated chat.
              </div>
            </div>
            {projects.length > 0 && (
              <button onClick={handleGetStarted} className="eai-btn-grn flex items-center px-3 py-2 text-sm flex-shrink-0 self-start">
                Add Project
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 p-5 overflow-y-auto">
          {isLoading ? (
            <LoadingIcon />
          ) : projects.length === 0 ? (
            <EmptyState onGetStarted={handleGetStarted} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {projects.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
};

interface EmptyStateProps {
  onGetStarted: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onGetStarted }) => (
  <div className="flex flex-col items-center justify-center h-full gap-4 py-16">
    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
      <BriefcaseIcon className="w-8 h-8 stroke-eai-primary-accent" />
    </div>
    <div className="text-gray-900 text-lg font-semibold font-Inter">No projects yet</div>
    <div className="text-gray-500 text-sm font-normal font-Inter text-center max-w-xs">
      Create your first project and let AI suggest the tasks you need to get started.
    </div>
    <button onClick={onGetStarted} className="eai-btn-grn flex items-center px-3 py-2 text-sm">
      Get Started with a Project
    </button>
  </div>
);

export default withAdminAuth(ProjectsPage);
